export const VIP_EXPIRE_PROMPT_KEY = "vip-expire-prompt"

const DAY_MS = 24 * 60 * 60 * 1000
const DAY_3 = 3 * DAY_MS
const DAY_7 = 7 * DAY_MS
const DAY_30 = 30 * DAY_MS

type WindowId = "d0_3" | "d3_7" | "d7_30"

type State = {
  expireAt: number
  userId?: string | number
  shown03: boolean
  shown37: boolean
  shown730: boolean
}

const emptyState = (expireAt = 0, userId?: string | number): State => ({
  expireAt,
  userId,
  shown03: false,
  shown37: false,
  shown730: false,
})

const readRaw = (key: string) => {
  if (typeof window === "undefined") return null
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

const writeRaw = (key: string, value: string) => {
  if (typeof window === "undefined") return
  try {
    localStorage.setItem(key, value)
  } catch { /* private mode */ }
}

const readState = (): State => {
  const raw = readRaw(VIP_EXPIRE_PROMPT_KEY)
  if (!raw) return emptyState()
  try {
    const parsed = JSON.parse(raw) as Partial<State>
    return {
      ...emptyState(),
      ...parsed,
      expireAt: Number(parsed.expireAt) || 0,
      shown03: !!parsed.shown03,
      shown37: !!parsed.shown37,
      shown730: !!parsed.shown730,
    }
  } catch {
    return emptyState()
  }
}

const writeState = (state: State) => writeRaw(VIP_EXPIRE_PROMPT_KEY, JSON.stringify(state))

const toMs = (expireAt: number) => (expireAt > 0 && expireAt < 1e12 ? expireAt * 1000 : expireAt)

/** 会员已过期（user_log.vip_expire_time 仅过期后才有值；0 表示无） */
export const isVipExpired = (expireAt?: number | null) => {
  const at = Number(expireAt) || 0
  if (!at) return false
  return Date.now() >= toMs(at)
}

/** 会员已过期且未满 30 天 */
export const isVipExpiredWithin30Days = (expireAt?: number | null) =>
  resolveVipExpireWindow(Number(expireAt) || 0) != null

/** 按 vip_expire_time 起算：0-3 / 3-7 / 7-30，满 30 天停止 */
export const resolveVipExpireWindow = (expireAt: number, now = Date.now()): WindowId | null => {
  if (!expireAt) return null
  const elapsed = now - toMs(expireAt)
  if (elapsed < 0 || elapsed >= DAY_30) return null
  if (elapsed < DAY_3) return "d0_3"
  if (elapsed < DAY_7) return "d3_7"
  return "d7_30"
}

const shownKey = (window: WindowId): keyof Pick<State, "shown03" | "shown37" | "shown730"> => {
  if (window === "d0_3") return "shown03"
  if (window === "d3_7") return "shown37"
  return "shown730"
}

const isSameTarget = (state: State, expireAt: number, userId?: string | number) => {
  if (Number(state.expireAt) !== Number(expireAt)) return false
  if (userId == null || state.userId == null) return true
  return String(state.userId) === String(userId)
}

/** 当前窗口尚未展示过则标记并返回 true */
export const consumeVipExpirePrompt = (expireAt: number, userId?: string | number): boolean => {
  if (typeof window === "undefined") return false
  const windowId = resolveVipExpireWindow(expireAt)
  if (!windowId) return false

  const state = readState()
  const current = isSameTarget(state, expireAt, userId) ? state : emptyState(expireAt, userId)
  const flag = shownKey(windowId)
  if (current[flag]) return false

  writeState({
    ...current,
    expireAt,
    userId: userId ?? current.userId,
    [flag]: true,
  })
  return true
}
